import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { LANGS, detectLang, getT, RATING_EMOJIS } from "@/lib/i18n";
import { MAGNIFLEX_LOGO_URL } from "@/lib/brand";
import { motion, AnimatePresence } from "framer-motion";

/* ---------------- helpers ---------------- */
function hexToRgba(hex, alpha = 1) {
  const m = String(hex || "").replace("#", "");
  if (m.length !== 6) return `rgba(0,0,0,${alpha})`;
  const r = parseInt(m.slice(0, 2), 16);
  const g = parseInt(m.slice(2, 4), 16);
  const b = parseInt(m.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/* ---------------- emoji rating ---------------- */
function EmojiRating({ rating, setRating, accent, primary }) {
  return (
    <div className="flex flex-col items-center gap-6" data-testid="emoji-rating">
      <div className="flex gap-3 sm:gap-4">
        {RATING_EMOJIS.map((emoji, idx) => {
          const value = idx + 1;
          const active = rating === value;
          return (
            <button
              type="button"
              key={value}
              onClick={() => setRating(value)}
              data-testid={`rating-${value}`}
              className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl border-2 transition-all duration-200 cursor-pointer flex flex-col items-center justify-center select-none gap-0.5"
              style={{
                borderColor: active ? primary : hexToRgba(primary, 0.15),
                backgroundColor: active ? hexToRgba(primary, 0.08) : "transparent",
                transform: active ? "scale(1.05)" : "scale(1)",
              }}
              aria-label={`Rating ${value}`}
            >
              <span className="text-2xl leading-none">{emoji}</span>
              <span
                className="text-[9px] font-bold leading-none"
                style={{ letterSpacing: "0.15em", color: active ? primary : hexToRgba(primary, 0.45) }}
              >
                {value}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------------- language switcher ---------------- */
function LanguageSwitcher({ lang, setLang, primary, mutedText, border }) {
  return (
    <div className="flex items-center gap-1 justify-center flex-wrap" data-testid="lang-selector">
      {LANGS.map((l) => {
        const active = l.code === lang;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => setLang(l.code)}
            title={l.label}
            data-testid={`lang-${l.code}`}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs transition-all border"
            style={{
              borderColor: active ? hexToRgba(primary, 0.5) : "transparent",
              backgroundColor: active ? hexToRgba(primary, 0.1) : "transparent",
              color: active ? primary : mutedText,
            }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.borderColor = border; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.borderColor = "transparent"; }}
          >
            <span className="text-base leading-none">{l.flag}</span>
            <span className="hidden sm:inline tracking-wide">{l.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- main page ---------------- */
export default function PublicLanding() {
  const { hotelSlug, citySlug } = useParams();
  const [lang, setLang] = useState(detectLang());
  const [hotel, setHotel] = useState(null);
  const [fatalError, setFatalError] = useState("");
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [email, setEmail] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [couponData, setCouponData] = useState(null);
  const [showShowcase, setShowShowcase] = useState(false);
  const [skippedCoupon, setSkippedCoupon] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState("");
  const t = useMemo(() => getT(lang), [lang]);

  useEffect(() => {
    api.get(`/public/landing/${hotelSlug}/${citySlug}`)
      .then(({ data }) => setHotel(data))
      .catch((e) => setFatalError(formatApiError(e)));
  }, [hotelSlug, citySlug]);

  useEffect(() => {
    localStorage.setItem("mgx_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  if (fatalError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4EFE6] px-6 text-center">
        <div>
          <img src={MAGNIFLEX_LOGO_URL} alt="Magniflex" className="h-12 mx-auto mb-6" />
          <div className="text-[#9E2A2B] text-sm">{fatalError}</div>
        </div>
      </div>
    );
  }
  if (!hotel) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">...</div>;

  /* Color tokens */
  const primary = hotel.primary_color || "#1B3A4B";
  const accent = hotel.secondary_color || "#C9A66B";
  const bg = hotel.background_color || "#F4EFE6";
  const textColor = hotel.text_color || "#1A1C20";
  const mutedText = hexToRgba(textColor, 0.55);
  const border = hexToRgba(textColor, 0.12);
  const cardBg = "#FFFFFF";
  const mutedBg = hexToRgba(textColor, 0.04);
  const headingFont = `'${hotel.font_family}', 'Manrope', system-ui, sans-serif`;

  const featured = hotel.featured_product;
  const discountLabel = hotel.coupon_discount_label || "";

  /* ---------------- handlers ---------------- */
  const submitFeedback = async (e) => {
    e.preventDefault();
    if (rating < 1) { setInlineError(t.error_required_rating); return; }
    setInlineError("");
    setSubmitting(true);
    try {
      await api.post(`/public/landing/${hotelSlug}/${citySlug}/feedback`, { rating, comment, language: lang });
      setFeedbackSent(true);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="offer-card"]');
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 350);
    } catch (err) { setInlineError(formatApiError(err)); }
    setSubmitting(false);
  };

  const submitCoupon = async (e) => {
    e.preventDefault();
    if (!email || !/\S+@\S+\.\S+/.test(email)) { setInlineError(t.error_email); return; }
    setInlineError("");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/landing/${hotelSlug}/${citySlug}/coupon-request`, { email, language: lang });
      setCouponData(data);
      setShowShowcase(true);
      setTimeout(() => {
        const el = document.querySelector('[data-testid="showcase-card"]');
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 350);
    } catch (err) { setInlineError(formatApiError(err)); }
    setSubmitting(false);
  };

  const skipCoupon = () => {
    setSkippedCoupon(true);
    setShowShowcase(true);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="showcase-card"]');
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
  };

  /* ---------------- render ---------------- */
  return (
    <div
      className="min-h-screen flex flex-col items-center px-5 sm:px-6 py-8 sm:py-12 relative overflow-hidden"
      style={{ background: bg, color: textColor, fontFamily: "'Manrope', system-ui, -apple-system, sans-serif" }}
      data-testid="public-landing"
    >
      {/* Soft background blurs */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden>
        <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full blur-[120px]" style={{ background: hexToRgba(primary, 0.08) }} />
        <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full blur-[100px]" style={{ background: hexToRgba(accent, 0.10) }} />
      </div>

      <div className="relative z-10 w-full max-w-lg">
        {/* HEADER */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-center gap-6 mb-10 sm:mb-12"
        >
          {/* Logos centered at top */}
          <div className="flex items-center justify-center gap-5 sm:gap-6 flex-wrap">
            {hotel.logo_url ? (
              <img src={hotel.logo_url} alt={hotel.name} className="h-12 sm:h-14 max-w-[160px] sm:max-w-[200px] object-contain" data-testid="hotel-logo" />
            ) : (
              <span className="text-sm font-light tracking-[0.3em] uppercase" style={{ color: textColor }} data-testid="hotel-name-text">{hotel.name}</span>
            )}
            <span className="w-px h-10 sm:h-12" style={{ background: border }} />
            <img src={MAGNIFLEX_LOGO_URL} alt="Magniflex" className="h-12 sm:h-14" data-testid="magniflex-logo" />
          </div>

          {/* Kicker line: hotel — magniflex */}
          <div className="flex items-center gap-4 text-xs tracking-[0.3em] uppercase font-light" style={{ color: mutedText }}>
            <span>{hotel.name}</span>
            <span className="w-6 h-px" style={{ background: border }} />
            <span>Magniflex</span>
          </div>

          {/* Language switcher */}
          <LanguageSwitcher lang={lang} setLang={setLang} primary={primary} mutedText={mutedText} border={border} />

          {/* Title */}
          <div className="text-center space-y-2 mt-2">
            <h1
              className="text-5xl md:text-6xl font-light leading-tight"
              style={{ fontFamily: headingFont, color: textColor }}
            >
              {t.how_slept}
            </h1>
            <p className="text-sm tracking-wide font-light" style={{ color: mutedText }}>
              {t.share_experience}
            </p>
          </div>

          {/* Decorative divider line — dot — line */}
          <div className="flex items-center gap-3 w-full max-w-xs">
            <div className="flex-1 h-px" style={{ background: border }} />
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: hexToRgba(primary, 0.6) }} />
            <div className="flex-1 h-px" style={{ background: border }} />
          </div>
        </motion.div>

        {/* MAIN CARD: feedback form + (after submit) offer subcard */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="rounded-2xl p-8 md:p-10 shadow-2xl"
          style={{ background: cardBg, border: `1px solid ${border}` }}
        >
          <form onSubmit={submitFeedback} className="flex flex-col gap-10">
            {/* Rating */}
            <div className="flex flex-col gap-4">
              <label className="text-xs tracking-widest uppercase text-center font-medium" style={{ color: mutedText }}>
                {t.rating_label}
              </label>
              <EmojiRating rating={rating} setRating={setRating} accent={accent} primary={primary} />
              <div className="min-h-[1.5rem] flex items-center justify-center">
                {rating === 0 && (
                  <span className="text-xs tracking-widest uppercase" style={{ color: mutedText }}>
                    {t.select_rating}
                  </span>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="h-px" style={{ background: border }} />

            {/* Comment */}
            <div className="flex flex-col gap-3">
              <label className="text-xs tracking-widest uppercase font-medium" style={{ color: mutedText }}>
                {t.comment_optional.split("(")[0].trim()} <span className="normal-case not-italic" style={{ opacity: 0.55 }}>(opzionale)</span>
              </label>
              <textarea
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={t.comment_placeholder}
                className="w-full rounded-xl px-4 py-3 text-sm resize-none focus:outline-none transition-all"
                style={{
                  background: mutedBg,
                  border: `1px solid ${border}`,
                  color: textColor,
                }}
                onFocus={(e) => { e.currentTarget.style.borderColor = primary; e.currentTarget.style.boxShadow = `0 0 0 1px ${primary}`; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = border; e.currentTarget.style.boxShadow = "none"; }}
                data-testid="feedback-comment"
                disabled={feedbackSent}
              />
            </div>

            {inlineError && !feedbackSent && (
              <div className="text-xs text-[#9E2A2B] text-center" data-testid="feedback-error">{inlineError}</div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting || feedbackSent || rating < 1}
              className="w-full py-4 rounded-xl text-sm tracking-widest uppercase font-medium transition-all duration-300"
              style={{
                background: feedbackSent ? mutedBg : (rating < 1 ? mutedBg : primary),
                color: feedbackSent ? mutedText : (rating < 1 ? mutedText : "#FFFFFF"),
                cursor: (submitting || feedbackSent || rating < 1) ? "not-allowed" : "pointer",
              }}
              data-testid="submit-feedback-btn"
            >
              {feedbackSent ? "✓ " + (lang === "it" ? "Inviato" : "Submitted") : (submitting ? t.sending : t.submit_review)}
            </button>

            <p className="text-[11px] text-center leading-relaxed px-2" style={{ color: hexToRgba(textColor, 0.4) }} data-testid="gdpr-text">
              {t.gdpr_consent.replace("{hotel}", hotel.name)}
            </p>

            {/* OFFER SUBCARD - revealed after feedback */}
            <AnimatePresence>
              {feedbackSent && !showShowcase && (
                <motion.div
                  key="offer"
                  initial={{ opacity: 0, y: 12, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, y: -8, height: 0 }}
                  transition={{ duration: 0.45 }}
                  className="rounded-xl p-5 flex flex-col gap-3"
                  style={{ border: `1px solid ${hexToRgba(primary, 0.3)}`, background: hexToRgba(primary, 0.05) }}
                  data-testid="offer-card"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="text-[10px] tracking-widest uppercase font-medium rounded-full px-2 py-0.5"
                      style={{ color: primary, border: `1px solid ${hexToRgba(primary, 0.4)}` }}
                    >
                      {t.exclusive_offer}
                    </span>
                    {discountLabel && (
                      <span
                        className="text-[10px] tracking-widest uppercase font-bold rounded-full px-2 py-0.5"
                        style={{ color: accent, border: `1px solid ${hexToRgba(accent, 0.5)}` }}
                      >
                        {discountLabel}
                      </span>
                    )}
                  </div>
                  <p className="text-xl font-light leading-snug" style={{ fontFamily: headingFont, color: textColor }}>
                    {featured?.name ? `Vuoi portare ${featured.name} a casa?` : t.take_home_title}
                  </p>
                  <p className="text-xs leading-relaxed" style={{ color: mutedText }}>{t.take_home_subtitle}</p>
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        e.stopPropagation();
                        submitCoupon(e);
                      }
                    }}
                    placeholder={t.email_placeholder}
                    className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none transition-all"
                    style={{ background: mutedBg, border: `1px solid ${border}`, color: textColor }}
                    onFocus={(ev) => { ev.currentTarget.style.borderColor = primary; ev.currentTarget.style.boxShadow = `0 0 0 1px ${primary}`; }}
                    onBlur={(ev) => { ev.currentTarget.style.borderColor = border; ev.currentTarget.style.boxShadow = "none"; }}
                    data-testid="coupon-email-input"
                  />
                  {inlineError && (
                    <div className="text-xs text-[#9E2A2B]" data-testid="coupon-error">{inlineError}</div>
                  )}
                  <button
                    type="button"
                    onClick={submitCoupon}
                    disabled={submitting}
                    className="w-full rounded-lg px-4 py-2 text-xs font-medium tracking-wider uppercase transition-all hover:opacity-90 disabled:opacity-50"
                    style={{ background: primary, color: "#FFFFFF" }}
                    data-testid="submit-coupon-btn"
                  >
                    {submitting ? t.sending : t.receive_discount}
                  </button>
                  <button
                    type="button"
                    onClick={skipCoupon}
                    className="text-[11px] hover:opacity-80 transition-colors underline underline-offset-2 text-left self-start"
                    style={{ color: mutedText }}
                    data-testid="skip-coupon-btn"
                  >
                    {t.no_thanks}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </form>
        </motion.div>

        {/* PRODUCT INFO CARD - always visible below the review (user request) */}
        {featured && (featured.image_url || featured.description || featured.tagline) && !showShowcase && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="rounded-2xl mt-6 overflow-hidden"
            style={{ background: cardBg, border: `1px solid ${border}` }}
            data-testid="product-info-card"
          >
            <div className="flex flex-col sm:flex-row">
              {featured.image_url && (
                <div className="w-full sm:w-2/5 aspect-square sm:aspect-auto" style={{ background: mutedBg }}>
                  <img src={featured.image_url} alt={featured.name} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex-1 p-5 sm:p-6 flex flex-col justify-center">
                <div className="text-[10px] tracking-[0.3em] uppercase font-medium mb-2" style={{ color: accent }}>
                  {featured.tagline || t.showcase_kicker}
                </div>
                <p className="text-2xl font-light leading-tight mb-2" style={{ fontFamily: headingFont, color: textColor }}>
                  {featured.name}
                </p>
                {featured.description && (
                  <p className="text-xs leading-relaxed" style={{ color: mutedText }}>
                    {featured.description}
                  </p>
                )}
                {(featured.height_cm || featured.firmness) && (
                  <div className="text-[11px] mt-3 tracking-wide" style={{ color: mutedText }}>
                    {featured.height_cm && <>Altezza {featured.height_cm} cm</>}
                    {featured.height_cm && featured.firmness && " · "}
                    {featured.firmness}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* SHOWCASE - revealed after coupon submitted or skipped */}
        <AnimatePresence>
          {showShowcase && featured && (
            <motion.div
              key="showcase"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.55 }}
              className="rounded-xl mt-6 overflow-hidden"
              style={{ background: cardBg, border: `1px solid ${border}` }}
              data-testid="showcase-card"
            >
              {/* Coupon confirmation banner */}
              {couponData && (
                <div className="p-5 border-b" style={{ background: hexToRgba(accent, 0.08), borderColor: border }} data-testid="coupon-confirmation">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] tracking-widest uppercase font-bold" style={{ color: accent }}>✓ {t.coupon_sent_title}</span>
                  </div>
                  <p className="text-xs leading-relaxed" style={{ color: mutedText }}>
                    {t.coupon_sent_subtitle}
                    {couponData.expires_at && <> · {t.expires_label} {new Date(couponData.expires_at).toLocaleDateString(lang === "zh" ? "zh-CN" : lang)}</>}
                  </p>
                </div>
              )}

              {/* Hero image with caption */}
              {featured.lifestyle_image_url ? (
                <div className="relative w-full aspect-[16/9] overflow-hidden">
                  <img src={featured.lifestyle_image_url} alt={featured.name} className="w-full h-full object-cover object-center" />
                  <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${hexToRgba(cardBg, 0.95)} 0%, ${hexToRgba(cardBg, 0.15)} 60%, transparent 100%)` }} />
                  <div className="absolute bottom-4 left-4 right-4">
                    <span className="text-[10px] tracking-[0.3em] uppercase font-medium" style={{ color: primary }}>{featured.name}</span>
                    <p className="text-2xl font-light leading-tight mt-0.5" style={{ fontFamily: headingFont, color: textColor }}>
                      {featured.tagline || t.showcase_kicker}
                    </p>
                  </div>
                </div>
              ) : featured.image_url ? (
                <div className="p-8 text-center" style={{ background: mutedBg }}>
                  <img src={featured.image_url} alt={featured.name} className="mx-auto max-h-[220px] object-contain" />
                  <span className="block mt-4 text-[10px] tracking-[0.3em] uppercase font-medium" style={{ color: primary }}>{featured.name}</span>
                  <p className="text-2xl font-light leading-tight mt-1" style={{ fontFamily: headingFont, color: textColor }}>
                    {featured.tagline || t.showcase_kicker}
                  </p>
                </div>
              ) : null}

              {/* Features 2x2 grid */}
              {featured.features && featured.features.length > 0 && (
                <div className="p-5 grid grid-cols-2 gap-3">
                  {featured.features.slice(0, 4).map((f, i) => (
                    <div key={i} className="rounded-lg p-3 flex flex-col gap-1.5" style={{ background: mutedBg }} data-testid={`feature-${i}`}>
                      <span className="text-lg leading-none" style={{ color: primary }}>{["↕", "✦", "◎", "≋"][i] || "•"}</span>
                      {f.title && <p className="text-xs font-medium leading-tight" style={{ color: textColor }}>{f.title}</p>}
                      {f.description && <p className="text-[11px] leading-relaxed" style={{ color: mutedText }}>{f.description}</p>}
                    </div>
                  ))}
                </div>
              )}

              {/* Pricing + CTA */}
              <div className="px-5 pb-5 pt-3 flex flex-col gap-3">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  {featured.price_from != null && (
                    <div>
                      <p className="text-[10px] tracking-widest uppercase" style={{ color: mutedText }}>{t.from_price}</p>
                      <p className="text-2xl font-light" style={{ fontFamily: headingFont, color: textColor }}>
                        {Number(featured.price_from).toLocaleString(lang === "zh" ? "zh-CN" : lang === "en" ? "en-US" : "it-IT")} €
                      </p>
                    </div>
                  )}
                  <div className="text-right">
                    {(featured.height_cm || featured.firmness) && (
                      <p className="text-[10px]" style={{ color: mutedText }}>
                        {featured.height_cm && <>Altezza {featured.height_cm} cm</>}
                        {featured.height_cm && featured.firmness && " · "}
                        {featured.firmness}
                      </p>
                    )}
                    <p className="text-[10px]" style={{ color: mutedText }}>{t.free_shipping}</p>
                  </div>
                </div>
                {featured.buy_link && (
                  <a
                    href={featured.buy_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full py-3 rounded-xl text-xs tracking-widest uppercase font-medium hover:opacity-90 transition-all duration-300 flex items-center justify-center gap-2"
                    style={{ background: primary, color: "#FFFFFF", boxShadow: `0 0 24px ${hexToRgba(primary, 0.25)}` }}
                    data-testid="buy-cta-btn"
                  >
                    <span>{t.discover_buy} {featured.name}</span>
                    <span className="text-xs opacity-70">↗</span>
                  </a>
                )}
                <p className="text-[10px] text-center" style={{ color: hexToRgba(textColor, 0.4) }}>{t.all_sizes_note}</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* No-featured fallback (after skip or coupon) */}
        {showShowcase && !featured && (
          <div className="mt-6 text-center" data-testid="thanks-card">
            <p className="text-3xl font-light" style={{ fontFamily: headingFont, color: textColor }}>{t.thanks_title}</p>
            <p className="text-sm mt-2" style={{ color: mutedText }}>{t.thanks_subtitle}</p>
          </div>
        )}

        {/* Footer */}
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.7 }}
          className="text-center text-xs tracking-wide mt-10"
          style={{ color: mutedText }}
        >
          {t.sleep_pleasure} — Magniflex®
        </motion.p>
      </div>
    </div>
  );
}
